/**
 * Custom hook for handling drag-and-drop with @dnd-kit
 */

import { useState } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { HookConfig, HookReorderItem } from '../../types/hooks';

interface UseHookDragDropOptions {
  onReorder: (items: HookReorderItem[]) => void;
}

export function useHookDragDrop({ onReorder }: UseHookDragDropOptions) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to activate drag
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);

    if (!over || active.id === over.id) {
      return; // No change
    }

    // Extract hook name and target stage from IDs
    // ID format: "hook-{stage}-{hook_name}"
    const activeHookName = (active.id as string).split('-').slice(2).join('-');
    const overStage = (over.id as string).split('-')[1];

    // Call the reorder callback with metadata
    onReorder([
      {
        hook_name: activeHookName,
        sort_order: 0, // Will be calculated by parent based on new position
      },
    ]);

    // Return metadata for parent to handle
    return {
      activeHookName,
      targetStage: overStage,
      overId: over.id as string,
    };
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setIsDragging(false);
  };

  return {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection: closestCenter,
  };
}

/**
 * Helper to calculate new sort orders after drag-and-drop
 */
export function calculateNewSortOrders(
  hooks: HookConfig[],
  movedHookName: string,
  targetStage: string,
  newIndex: number
): HookReorderItem[] {
  // Filter hooks for target stage
  const stageHooks = hooks.filter((h) => h.stage === targetStage);

  // Remove the moved hook
  const otherHooks = stageHooks.filter((h) => h.hook_name !== movedHookName);

  // Insert at new index
  otherHooks.splice(newIndex, 0, hooks.find((h) => h.hook_name === movedHookName)!);

  // Calculate new sort orders (spacing by 10)
  return otherHooks.map((hook, index) => ({
    hook_name: hook.hook_name,
    sort_order: (index + 1) * 10,
  }));
}
